import { BlogPost } from '@commerce/types/blog'
import { HoskingsButton } from '@components/ui'
import cn from 'clsx'
import Link from 'next/link'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import ArticleCard from '../ArticleCard'

interface Props {
  title: string
  articles: BlogPost[]
  hideWrapper?: Boolean
}

const ArticlesGrid: FC<Props> = ({ title, articles, hideWrapper }) => {
  const buttonTitle = 'Explore the journal'
  const sliderRef = useRef<any>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesVisible, setSlidesVisible] = useState(() => {
    if (typeof window === 'undefined') return 2.5
    return window.innerWidth < 768 ? 1.5 : 2.5
  })
  const [isAtEnd, setIsAtEnd] = useState(false)

  useEffect(() => {
    const updateSlidesVisible = () => {
      const width = window.innerWidth
      setSlidesVisible(width < 768 ? 1.5 : 2.5)
    }

    updateSlidesVisible()
    window.addEventListener('resize', updateSlidesVisible)

    return () => {
      window.removeEventListener('resize', updateSlidesVisible)
    }
  }, [])

  const updateEndState = useCallback(
    (currentSlide: number) => {
      const maxStartIndex = Math.max(0, articles.length - slidesVisible)
      setIsAtEnd(currentSlide >= maxStartIndex - 0.01)
    },
    [articles.length, slidesVisible]
  )

  useEffect(() => {
    updateEndState(0)
  }, [updateEndState])

  useEffect(() => {
    setCurrentSlide(0)
    setIsAtEnd(false)
  }, [slidesVisible])

  const scrollBy = slidesVisible <= 1.5 ? 1 : 2
  const dotCount = Math.ceil(articles.length / scrollBy)
  const activeDot = isAtEnd
    ? dotCount - 1
    : Math.max(0, Math.min(Math.floor(currentSlide / scrollBy), dotCount - 1))

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    initialSlide: 0,
    speed: 500,
    slidesToShow: 2.5,
    slidesToScroll: 2,
    beforeChange: (_: number, nextSlide: number) => {
      setCurrentSlide(nextSlide)
      updateEndState(nextSlide)
    },
    afterChange: (slide: number) => {
      setCurrentSlide(slide)
      updateEndState(slide)
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
        },
      },
    ],
  }

  const sliderKey = `${slidesVisible}-${articles.length}`

  return (
    <div className={cn('my-10 laptop:my-20', { wrapper: !hideWrapper })}>
      <div className="uppercase text-text-dark font-normal pb-6 laptop:pb-7 text-mobileh4 md:text-h4">
        {title}
      </div>

      <div
        className={cn('laptop:hidden grid-slick', {
          'grid-slick-at-end': isAtEnd,
        })}
      >
        <Slider ref={sliderRef} key={sliderKey} {...sliderSettings}>
          {articles.map((article: BlogPost) => {
            return (
              <ArticleCard
                key={article.id}
                handle={article?.handle}
                title={article?.title}
                image={article?.image}
                excerpt={article?.excerpt}
              />
            )
          })}
        </Slider>
        {dotCount > 1 && (
          <ul className="slick-dots">
            {Array.from({ length: dotCount }, (_, i) => (
              <li key={i} className={i === activeDot ? 'slick-active' : ''}>
                <button
                  type="button"
                  onClick={() => sliderRef.current?.slickGoTo(i * scrollBy)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="hidden laptop:flex justify-between gap-6 laptop:gap-8">
        {articles.map((article: BlogPost) => {
          return (
            <ArticleCard
              key={article.id}
              handle={article?.handle}
              title={article?.title}
              image={article?.image}
              excerpt={article?.excerpt}
            />
          )
        })}
      </div>

      <div className="pt-10 flex justify-center">
        <Link href={`/journal`}>
          <HoskingsButton variant="transparent" size="medium">
            {buttonTitle}
          </HoskingsButton>
        </Link>
      </div>
    </div>
  )
}

export default ArticlesGrid
